import { useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Users, Pencil, X, Check, Upload, CreditCard, Clock } from 'lucide-react';
import { useOrg } from '../../hooks/useOrganizations';
import { useOrgUsers } from '../../hooks/useOrgUsers';
import { useOrgSubscription, OrgSubscriptionDetails } from '../../hooks/useOrgSubscription';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { Organization } from '../../types/admin';

// ── Edit Form ──────────────────────────────────────────────────────────────

interface EditFormProps {
  org: Organization;
  onSave: (data: Partial<Omit<Organization, 'id' | 'createdAt' | 'updatedAt'>>) => Promise<void>;
  onCancel: () => void;
}

function EditForm({ org, onSave, onCancel }: EditFormProps) {
  const [name, setName] = useState(org.name);
  const [slug, setSlug] = useState(org.slug ?? '');
  const [logoBase64, setLogoBase64] = useState<string | null>(org.logoBase64 ?? null);
  const [isDemo, setIsDemo] = useState(org.isDemo ?? false);
  // Operations = combined Dispatch + Jobs (both on or both off)
  const [allowedFeatureOperations, setAllowedFeatureOperations] = useState(
    org.allowedFeatureDispatch !== false && org.allowedFeatureJobs !== false,
  );
  const [allowedFeatureFleetManagement, setAllowedFeatureFleetManagement] = useState(org.allowedFeatureFleetManagement ?? false);
  const [allowedFeatureClientPortal, setAllowedFeatureClientPortal] = useState(org.allowedFeatureClientPortal !== false);
  const [allowedFeatureQuotePdfs, setAllowedFeatureQuotePdfs] = useState(org.allowedFeatureQuotePdfs !== false);
  const [allowedFeatureSmartMovingIntegration, setAllowedFeatureSmartMovingIntegration] = useState(org.allowedFeatureSmartMovingIntegration ?? false);
  const [allowedFeatureCommercialMovingQuotes, setAllowedFeatureCommercialMovingQuotes] = useState(org.allowedFeatureCommercialMovingQuotes !== false);
  const [allowedFeatureResidentialMovingQuotes, setAllowedFeatureResidentialMovingQuotes] = useState(org.allowedFeatureResidentialMovingQuotes ?? false);
  const [allowedFeatureLeadPipeline, setAllowedFeatureLeadPipeline] = useState(org.allowedFeatureLeadPipeline ?? false);
  const [allowedFeatureOnlineLeadCapture, setAllowedFeatureOnlineLeadCapture] = useState(org.allowedFeatureOnlineLeadCapture ?? false);
  const [allowedFeatureStripeConnect, setAllowedFeatureStripeConnect] = useState(org.allowedFeatureStripeConnect ?? false);
  const [allowedFeatureCustomerPayments, setAllowedFeatureCustomerPayments] = useState(org.allowedFeatureCustomerPayments ?? false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  function handleLogoFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setLogoBase64(reader.result as string);
    reader.readAsDataURL(file);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) { setError('Organization name is required.'); return; }
    setError('');
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        slug: slug.trim().toLowerCase() || undefined,
        logoBase64: logoBase64 ?? '',
        isDemo,
        // Operations toggle maps to Dispatch + Jobs
        allowedFeatureDispatch: allowedFeatureOperations,
        allowedFeatureJobs: allowedFeatureOperations,
        // Fleet Management is independent
        allowedFeatureFleetManagement,
        allowedFeatureClientPortal,
        allowedFeatureQuotePdfs,
        allowedFeatureSmartMovingIntegration,
        allowedFeatureCommercialMovingQuotes,
        allowedFeatureResidentialMovingQuotes,
        allowedFeatureLeadPipeline,
        allowedFeatureOnlineLeadCapture,
        allowedFeatureStripeConnect,
        allowedFeatureCustomerPayments,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save changes.');
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5 lg:grid lg:grid-cols-2 lg:gap-x-8 lg:gap-y-5">
      {error && (
        <div className="lg:col-span-2 px-4 py-3 rounded-lg bg-rose/10 border border-rose/30 text-small text-rose">
          {error}
        </div>
      )}

      <h3 className="hidden md:block font-display text-h3 text-midnight lg:col-span-2">Edit Organization</h3>

      {/* ── Left column on lg+ ── */}
      <div className="flex flex-col gap-5">
        {/* Name */}
        <div>
          <label
            htmlFor="edit-name"
            className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1"
          >
            Organization Name <span className="text-rose">*</span>
          </label>
          <input
            id="edit-name"
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                       text-small text-midnight placeholder:text-slate
                       focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                       transition-colors duration-150"
          />
        </div>

        {/* Slug */}
        <div>
          <label
            htmlFor="edit-slug"
            className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1"
          >
            Booking URL Slug
          </label>
          <input
            id="edit-slug"
            type="text"
            value={slug}
            onChange={e => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            placeholder="e.g. abc-moving"
            className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                       text-small text-midnight placeholder:text-slate font-mono
                       focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                       transition-colors duration-150"
          />
          {slug && (
            <p className="text-[11px] text-slate mt-1">
              booking.iomipro.com/<span className="font-semibold text-midnight">{slug}</span>
            </p>
          )}
        </div>

        {/* Logo */}
        <div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-1">Logo</p>
          {logoBase64 ? (
            <div className="flex items-center gap-3">
              <img
                src={logoBase64}
                alt="Logo"
                className="h-16 w-16 rounded-xl object-contain border border-stone bg-mist p-1"
              />
              <button
                type="button"
                onClick={() => { setLogoBase64(null); if (fileRef.current) fileRef.current.value = ''; }}
                className="text-[13px] text-slate hover:text-rose transition-colors"
              >
                Remove
              </button>
            </div>
          ) : (
            <label
              className="flex flex-col items-center justify-center h-20 border-2 border-dashed
                         border-stone rounded-xl cursor-pointer bg-mist
                         hover:border-plum hover:bg-plum/5 transition-colors duration-150"
            >
              <Upload size={18} className="text-slate" />
              <span className="text-[13px] text-slate mt-1">Upload PNG</span>
              <input
                ref={fileRef}
                type="file"
                accept=".png,image/png"
                className="sr-only"
                onChange={handleLogoFile}
              />
            </label>
          )}
        </div>
      </div>

      {/* ── Right column on lg+ ── */}
      <div className="flex flex-col gap-5">
        {/* Demo Org */}
        <div className="flex items-center justify-between bg-white border border-stone rounded-xl px-4 py-3.5">
          <div>
            <p className="text-[13px] font-semibold text-midnight">Demo Organization</p>
            <p className="text-[12px] text-slate mt-0.5 leading-snug">
              Excluded from production stats when filtered.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={isDemo}
            onClick={() => setIsDemo(v => !v)}
            className={`relative shrink-0 ml-4 w-11 h-6 rounded-full transition-colors duration-200
              ${isDemo ? 'bg-plum' : 'bg-stone'}`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200
                ${isDemo ? 'translate-x-5' : 'translate-x-0'}`}
            />
          </button>
        </div>

        {/* Allowed Features */}
        <div className="border border-stone rounded-xl overflow-hidden">
          <div className="px-4 py-3 bg-mist border-b border-stone">
            <p className="text-[13px] font-semibold text-midnight">Allowed Features</p>
            <p className="text-[12px] text-slate mt-0.5 leading-snug">
              Controls which features this org is permitted to use.
            </p>
          </div>
          <div className="divide-y divide-stone">
            {([
              { label: 'Commercial Moving Quotes', desc: 'Allow commercial moving quote functionality.', value: allowedFeatureCommercialMovingQuotes, set: setAllowedFeatureCommercialMovingQuotes },
              { label: 'Residential Moving Quotes', desc: 'Allow residential moving quote functionality.', value: allowedFeatureResidentialMovingQuotes, set: setAllowedFeatureResidentialMovingQuotes },
              { label: 'Lead Pipeline', desc: 'Allow leads page, lead tracking, and follow-up management.', value: allowedFeatureLeadPipeline, set: setAllowedFeatureLeadPipeline },
              { label: 'Online Lead Capture', desc: 'Public booking form for capturing leads via website. Requires Lead Pipeline.', value: allowedFeatureOnlineLeadCapture, set: setAllowedFeatureOnlineLeadCapture },
              { label: 'Operations', desc: 'Dispatch screen, Jobs page, mover role, and Dashboard Jobs tab.', value: allowedFeatureOperations, set: setAllowedFeatureOperations },
              { label: 'Fleet Management', desc: 'Truck and warehouse fleet tracking, location management, and dispatch vehicle assignment.', value: allowedFeatureFleetManagement, set: setAllowedFeatureFleetManagement },
              { label: 'Client Portal', desc: 'Allow Online Quote buttons on quote versions.', value: allowedFeatureClientPortal, set: setAllowedFeatureClientPortal },
              { label: 'Quote PDFs', desc: 'Allow PDF generation when creating quote versions.', value: allowedFeatureQuotePdfs, set: setAllowedFeatureQuotePdfs },
              { label: 'SmartMoving Integration', desc: 'Allow this org to connect and sync with SmartMoving CRM.', value: allowedFeatureSmartMovingIntegration, set: setAllowedFeatureSmartMovingIntegration },
              { label: 'Stripe Connect', desc: 'Allow this org to accept payments via Stripe Connect.', value: allowedFeatureStripeConnect, set: setAllowedFeatureStripeConnect },
              { label: 'Customer Payments', desc: 'Allow this org to configure and accept customer payments (offline + Stripe).', value: allowedFeatureCustomerPayments, set: setAllowedFeatureCustomerPayments },
            ] as const).map(flag => (
              <div key={flag.label} className="flex items-center justify-between px-4 py-3">
                <div>
                  <p className="text-[13px] font-semibold text-midnight">{flag.label}</p>
                  <p className="text-[12px] text-slate mt-0.5 leading-snug">{flag.desc}</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={flag.value}
                  onClick={() => flag.set(v => !v)}
                  className={`relative shrink-0 ml-4 w-11 h-6 rounded-full transition-colors duration-200
                    ${flag.value ? 'bg-plum' : 'bg-stone'}`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200
                      ${flag.value ? 'translate-x-5' : 'translate-x-0'}`}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Actions — full width */}
      <div className="lg:col-span-2 flex gap-3 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 h-11 rounded-lg border border-stone text-small text-slate font-semibold
                     hover:border-midnight hover:text-midnight active:bg-mist
                     transition-colors duration-150"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="flex-1 h-11 rounded-lg bg-plum text-white text-small font-semibold
                     hover:bg-rose active:opacity-80 disabled:opacity-50
                     transition-colors duration-150 flex items-center justify-center gap-2"
        >
          {saving ? 'Saving…' : <><Check size={16} />Save</>}
        </button>
      </div>
    </form>
  );
}

// ── Subscription Details ────────────────────────────────────────────────────

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-emerald-50 text-emerald-700',
  trialing: 'bg-sky-50 text-sky-700',
  past_due: 'bg-amber-50 text-amber-700',
  canceled: 'bg-rose/10 text-rose',
  incomplete: 'bg-stone text-slate',
  unpaid: 'bg-rose/10 text-rose',
};

function statusLabel(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatCurrency(amount: number, currency: string): string {
  const sym = currency === 'eur' ? '€' : currency === 'gbp' ? '£' : '$';
  return `${sym}${(amount / 100).toFixed(2)}`;
}

function formatUnixDate(ts: number): string {
  return new Date(ts * 1000).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function daysUntil(unixSeconds: number): number {
  return Math.max(0, Math.ceil((unixSeconds * 1000 - Date.now()) / (1000 * 60 * 60 * 24)));
}

function SubscriptionSection({ sub }: { sub: OrgSubscriptionDetails }) {
  const isFlatRate = !!(sub.priceNickname?.toLowerCase().includes('flat'));
  const isFree = sub.totalAmount === 0;
  const isTrialing = sub.status === 'trialing';
  const trialDaysLeft = isTrialing && sub.trialEnd ? daysUntil(sub.trialEnd) : null;

  return (
    <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
      <div className="px-4 py-3 bg-mist border-b border-stone flex items-center gap-2">
        <CreditCard size={16} className="text-plum" />
        <span className="text-[11px] font-bold uppercase tracking-widest text-slate">
          Subscription Details
        </span>
      </div>

      <div className="px-4 py-3 flex flex-col gap-3">
        {/* Status + badges */}
        <div className="flex items-center justify-between">
          <span className="text-[13px] text-slate">Status</span>
          <div className="flex items-center gap-1.5">
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold
                          uppercase tracking-wide leading-none
                          ${STATUS_STYLES[sub.status] ?? 'bg-stone text-slate'}`}
            >
              {statusLabel(sub.status)}
            </span>
            {isFree && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold
                              uppercase tracking-wide leading-none text-green-700 bg-green-50 border border-green-200">
                Free
              </span>
            )}
          </div>
        </div>

        {/* Product name */}
        {sub.productName && (
          <div className="flex items-center justify-between">
            <span className="text-[13px] text-slate">Plan</span>
            <span className="text-[13px] font-semibold text-midnight">{sub.productName}</span>
          </div>
        )}

        {/* Trial info */}
        {isTrialing && (
          <div className={`px-3 py-2 rounded-lg flex items-center gap-2 text-[13px] leading-snug
                          ${trialDaysLeft !== null && trialDaysLeft <= 3
                            ? 'bg-red-50 border border-red-200 text-red-800'
                            : 'bg-sky-50 border border-sky-200 text-sky-800'}`}>
            <Clock size={14} className="shrink-0" />
            {trialDaysLeft !== null ? (
              <span>
                <span className="font-semibold">{trialDaysLeft} {trialDaysLeft === 1 ? 'day' : 'days'}</span> remaining in trial
                {sub.trialEnd && <span className="text-[12px]"> — billing starts {formatUnixDate(sub.trialEnd)}</span>}
              </span>
            ) : (
              <span>Trial period active</span>
            )}
          </div>
        )}

        {/* Seats / users */}
        <div className="flex items-center justify-between">
          <span className="text-[13px] text-slate">Users</span>
          <span className="text-[13px] font-semibold text-midnight">{sub.subscriptionQuantity}</span>
        </div>

        {/* Pricing row — flat-rate vs per-seat */}
        {sub.unitAmount != null && sub.currency && (
          isFlatRate ? (
            <div className="flex items-center justify-between">
              <span className="text-[13px] text-slate">Flat rate</span>
              <span className={`text-[13px] font-semibold ${isFree ? 'text-green-700' : 'text-midnight'}`}>
                {isFree ? 'Free' : `${formatCurrency(sub.unitAmount, sub.currency)}/${sub.interval ?? 'mo'}`}
              </span>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <span className="text-[13px] text-slate">Per-user price</span>
                <span className="text-[13px] font-semibold text-midnight">
                  {formatCurrency(sub.unitAmount, sub.currency)}/{sub.interval ?? 'mo'}
                </span>
              </div>
              {sub.totalAmount != null && (
                <div className="flex items-center justify-between">
                  <span className="text-[13px] text-slate">Total</span>
                  <span className="text-[13px] font-semibold text-midnight">
                    {formatCurrency(sub.totalAmount, sub.currency)}/{sub.interval ?? 'mo'}
                  </span>
                </div>
              )}
            </>
          )
        )}

        {/* Current period */}
        {sub.currentPeriodStart && sub.currentPeriodEnd && (
          <div className="flex items-center justify-between">
            <span className="text-[13px] text-slate">Current period</span>
            <span className="text-[13px] font-semibold text-midnight">
              {formatUnixDate(sub.currentPeriodStart)} – {formatUnixDate(sub.currentPeriodEnd)}
            </span>
          </div>
        )}

        {/* Cancel at period end */}
        {sub.cancelAtPeriodEnd && (
          <div className="px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-[13px] text-amber-800 leading-snug">
            Cancels at end of current billing period
          </div>
        )}

        {/* Customer email */}
        {sub.customerEmail && (
          <div className="flex items-center justify-between pt-2 border-t border-stone">
            <span className="text-[13px] text-slate">Billing email</span>
            <span className="text-[13px] font-semibold text-midnight truncate max-w-[200px]">
              {sub.customerEmail}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────

export function OrgDetailsPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const { org, loading, error, updateOrg } = useOrg(orgId!);
  const { users, loading: usersLoading } = useOrgUsers(orgId!);
  const { subscription, loading: subLoading } = useOrgSubscription(orgId!);
  const [editing, setEditing] = useState(false);

  async function handleSave(
    data: Partial<Omit<Organization, 'id' | 'createdAt' | 'updatedAt'>>,
  ) {
    await updateOrg(data);
    setEditing(false);
  }

  return (
    <div className="px-4 pt-6 pb-12 md:px-8 md:max-w-3xl lg:max-w-5xl md:mx-auto">
      {/* Back */}
      <button
        type="button"
        onClick={() => navigate('/admin/platform-tools/organizations')}
        className="flex items-center gap-1 text-small text-slate hover:text-midnight mb-4
                   transition-colors duration-150 -ml-1"
      >
        <ChevronLeft size={16} />
        Organizations
      </button>

      {loading && (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {!loading && error && (
        <div className="flex flex-col items-center py-16 gap-2 text-center">
          <p className="text-small text-rose font-semibold">Failed to load organization</p>
          <p className="text-small text-slate">{error}</p>
        </div>
      )}

      {!loading && !error && org && (
        <>
          {/* Header — always full width */}
          <div className="flex items-start gap-4 mb-6">
            {org.logoBase64 ? (
              <img
                src={org.logoBase64}
                alt={org.name}
                className="shrink-0 w-16 h-16 rounded-xl object-contain border border-stone bg-mist p-1"
              />
            ) : (
              <div className="shrink-0 w-16 h-16 rounded-xl bg-plum/10 flex items-center justify-center">
                <span className="font-display font-bold text-2xl text-plum">
                  {org.name.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="font-display text-h1 text-midnight leading-tight">{org.name}</h1>
                {org.isDemo && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold
                                   uppercase tracking-wide bg-amber-100 text-amber-700 leading-none mt-0.5">
                    Demo
                  </span>
                )}
              </div>
            </div>
            {!editing && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="shrink-0 h-9 px-3 rounded-lg border border-stone text-slate text-[13px]
                           font-semibold flex items-center gap-1.5
                           hover:border-plum hover:text-plum active:bg-mist
                           transition-colors duration-150"
              >
                <Pencil size={14} />
                Edit
              </button>
            )}
            {editing && (
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="shrink-0 h-9 w-9 rounded-lg border border-stone text-slate flex items-center justify-center
                           hover:border-midnight hover:text-midnight active:bg-mist
                           transition-colors duration-150"
                aria-label="Cancel edit"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {!editing && (
            <div className="flex flex-col gap-5 lg:grid lg:grid-cols-2 lg:gap-x-8 lg:gap-y-5 lg:items-start">
              {/* ── Left column: Subscription + Manage Users ── */}
              <div className="flex flex-col gap-5">
              {subLoading && (
                <div className="bg-white border border-stone rounded-xl p-6 shadow-brand-sm flex justify-center">
                  <LoadingSpinner size="sm" />
                </div>
              )}
              {!subLoading && subscription && (
                <SubscriptionSection sub={subscription} />
              )}
              {!subLoading && !subscription && (
                <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
                  <div className="px-4 py-3 bg-mist border-b border-stone flex items-center gap-2">
                    <CreditCard size={16} className="text-plum" />
                    <span className="text-[11px] font-bold uppercase tracking-widest text-slate">
                      Subscription
                    </span>
                  </div>
                  <div className="px-4 py-4">
                    <p className="text-[13px] text-slate italic">No active subscription</p>
                  </div>
                </div>
              )}

              {/* Manage Users card */}
              <button
                type="button"
                onClick={() => navigate(`/admin/platform-tools/organizations/${orgId}/users`)}
                className="w-full text-left bg-white border border-stone rounded-xl p-4 shadow-brand-sm
                           flex items-center gap-4
                           hover:border-plum/40 hover:shadow-brand-md active:bg-mist
                           transition-all duration-150"
              >
                <div className="shrink-0 w-10 h-10 rounded-lg bg-plum/10 flex items-center justify-center">
                  <Users size={20} className="text-plum" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-small text-midnight">
                    Manage Users
                    {!usersLoading && (
                      <span className="ml-2 text-plum font-bold">{users.filter(u => u.status !== 'inactive').length}</span>
                    )}
                  </p>
                  <p className="text-[13px] text-slate mt-0.5">View and manage this org's users.</p>
                </div>
                <ChevronLeft size={18} className="shrink-0 text-slate rotate-180" />
              </button>
              </div>{/* end left column */}

              {/* ── Right column: Allowed Features ── */}
              <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
                <div className="px-4 py-3 bg-mist border-b border-stone">
                  <span className="text-[11px] font-bold uppercase tracking-widest text-slate">
                    Allowed Features
                  </span>
                </div>
                <div className="divide-y divide-stone">
                  {([
                    { key: 'allowedFeatureCommercialMovingQuotes' as const, label: 'Commercial Moving Quotes', desc: 'Commercial moving quote functionality.', defaultOn: true },
                    { key: 'allowedFeatureResidentialMovingQuotes' as const, label: 'Residential Moving Quotes', desc: 'Residential moving quote functionality.', defaultOn: false },
                    { key: 'allowedFeatureLeadPipeline' as const, label: 'Lead Pipeline', desc: 'Leads page, lead tracking, and follow-up management.', defaultOn: false },
                    { key: 'allowedFeatureOnlineLeadCapture' as const, label: 'Online Lead Capture', desc: 'Public booking form for capturing leads via website.', defaultOn: false },
                    { key: 'allowedFeatureDispatch' as const, label: 'Operations', desc: 'Dispatch, Jobs, mover role, and Dashboard Jobs tab.', defaultOn: true },
                    { key: 'allowedFeatureFleetManagement' as const, label: 'Fleet Management', desc: 'Truck and warehouse fleet tracking, location management, and dispatch vehicle assignment.', defaultOn: false },
                    { key: 'allowedFeatureClientPortal' as const, label: 'Client Portal', desc: 'Online Quote buttons on quote versions.', defaultOn: true },
                    { key: 'allowedFeatureQuotePdfs' as const, label: 'Quote PDFs', desc: 'Generate PDFs when creating quote versions.', defaultOn: true },
                    { key: 'allowedFeatureSmartMovingIntegration' as const, label: 'SmartMoving Integration', desc: 'Connect and sync with SmartMoving CRM.', defaultOn: false },
                    { key: 'allowedFeatureStripeConnect' as const, label: 'Stripe Connect', desc: 'Accept payments via Stripe Connect.', defaultOn: false },
                    { key: 'allowedFeatureCustomerPayments' as const, label: 'Customer Payments', desc: 'Configure and accept customer payments (offline + Stripe).', defaultOn: false },
                  ] as const).map(f => {
                    const on = f.defaultOn
                      ? org[f.key] !== false
                      : (org[f.key] ?? false);
                    return (
                      <div key={f.key} className="flex items-center justify-between px-4 py-3">
                        <div>
                          <p className="text-[13px] font-semibold text-midnight">{f.label}</p>
                          <p className="text-[12px] text-slate mt-0.5 leading-snug">{f.desc}</p>
                        </div>
                        <div
                          aria-disabled="true"
                          className={`relative shrink-0 ml-4 w-11 h-6 rounded-full opacity-60
                            ${on ? 'bg-plum' : 'bg-stone'}`}
                        >
                          <span
                            className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow
                              ${on ? 'translate-x-5' : 'translate-x-0'}`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

            </div>
          )}

          {editing && (
            <div>
              <EditForm org={org} onSave={handleSave} onCancel={() => setEditing(false)} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
